import { useEditor, EditorContent, Extension } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { TextStyle, FontSize, Color } from "@tiptap/extension-text-style"
import data from "@emoji-mart/data"
import Picker from "@emoji-mart/react"
import { useState, useEffect, useRef } from "react"
import styles from "./minieditor.module.css"

// TextAlign customizado — sem dependência de pacote externo
const TextAlign = Extension.create({
    name: "textAlign",
    addOptions() {
        return { types: ["paragraph"], defaultAlignment: "left" }
    },
    addGlobalAttributes() {
        return [{
            types: this.options.types,
            attributes: {
                textAlign: {
                    default: this.options.defaultAlignment,
                    parseHTML: el => el.style.textAlign || this.options.defaultAlignment,
                    renderHTML: ({ textAlign }) =>
                        textAlign && textAlign !== this.options.defaultAlignment
                            ? { style: `text-align: ${textAlign}` }
                            : {},
                },
            },
        }]
    },
    addCommands() {
        return {
            setTextAlign: alignment => ({ commands }) =>
                this.options.types.every(type =>
                    commands.updateAttributes(type, { textAlign: alignment })
                ),
        }
    },
})

const NoNewLine = Extension.create({
    name: "noNewLine",
    addKeyboardShortcuts() {
        return { Enter: () => true, "Shift-Enter": () => true }
    },
})

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"]

export default function MiniEditor({ value, onChange, singleLine = false }) {
    const [showEmoji, setShowEmoji] = useState(false)
    const [fontSize, setFontSize] = useState("16px")
    const [color, setColor] = useState("#1e293b")
    const emojiRef = useRef(null)

    useEffect(() => {
        function onOutside(e) {
            if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false)
        }
        if (showEmoji) document.addEventListener("mousedown", onOutside)
        return () => document.removeEventListener("mousedown", onOutside)
    }, [showEmoji])

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
            }),
            TextStyle,
            FontSize,
            Color,
            TextAlign,
            ...(singleLine ? [NoNewLine] : []),
        ],
        content: value || "<p></p>",
        onUpdate({ editor }) {
            onChange(editor.getHTML())
        },
    })

    useEffect(() => {
        if (!editor) return
        if (value === editor.getHTML()) return
        editor.commands.setContent(value || "")
    }, [value, editor])

    if (!editor) return null

    function applyFontSize(size) {
        setFontSize(size)
        const { from, to } = editor.state.selection
        if (from !== to) {
            editor.chain().focus().setFontSize(size).run()
        } else {
            editor.chain().focus().selectAll().setFontSize(size).run()
        }
    }

    function applyColor(c) {
        setColor(c)
        const { from, to } = editor.state.selection
        if (from !== to) {
            editor.chain().focus().setColor(c).run()
        } else {
            editor.chain().focus().selectAll().setColor(c).run()
        }
    }

    function isAlign(dir) {
        return editor.isActive("paragraph", { textAlign: dir })
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.toolbar}>

                <select className={styles.select} value={fontSize}
                    onChange={e => applyFontSize(e.target.value)} title="Tamanho da fonte">
                    {FONT_SIZES.map(s => (
                        <option key={s} value={s}>{s.replace("px", "")}px</option>
                    ))}
                </select>

                <label className={styles.colorLabel} title="Cor da fonte">
                    <span className={styles.colorA} style={{ borderBottom: `3px solid ${color}` }}>A</span>
                    <input type="color" value={color}
                        onChange={e => applyColor(e.target.value)}
                        className={styles.colorInput} />
                </label>

                <div className={styles.sep} />

                <button type="button"
                    className={`${styles.btn} ${styles.bold} ${editor.isActive("bold") ? styles.active : ""}`}
                    onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito">B</button>
                <button type="button"
                    className={`${styles.btn} ${styles.italic} ${editor.isActive("italic") ? styles.active : ""}`}
                    onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico">I</button>

                <div className={styles.sep} />

                <button type="button"
                    className={`${styles.btn} ${isAlign("left") ? styles.active : ""}`}
                    onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Esquerda">
                    <AlignLeftIcon />
                </button>
                <button type="button"
                    className={`${styles.btn} ${isAlign("center") ? styles.active : ""}`}
                    onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Centralizar">
                    <AlignCenterIcon />
                </button>
                <button type="button"
                    className={`${styles.btn} ${isAlign("right") ? styles.active : ""}`}
                    onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Direita">
                    <AlignRightIcon />
                </button>

                <div className={styles.sep} />

                <div className={styles.emojiWrap} ref={emojiRef}>
                    <button type="button" className={styles.btn}
                        onClick={() => setShowEmoji(o => !o)} title="Inserir emoji">🙂</button>
                    {showEmoji && (
                        <div className={styles.emojiPicker}>
                            <Picker data={data} locale="pt"
                                onEmojiSelect={em => {
                                    editor.chain().focus().insertContent(em.native).run()
                                    setShowEmoji(false)
                                }} />
                        </div>
                    )}
                </div>

            </div>

            <EditorContent editor={editor}
                className={singleLine ? styles.editorSingle : styles.editor} />
        </div>
    )
}

function AlignLeftIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="15" y2="12" /><line x1="3" y1="18" x2="18" y2="18" />
        </svg>
    )
}
function AlignCenterIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="4" y1="18" x2="20" y2="18" />
        </svg>
    )
}
function AlignRightIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="9" y1="12" x2="21" y2="12" /><line x1="6" y1="18" x2="21" y2="18" />
        </svg>
    )
}
